import React from 'react';
import classNames from 'classnames';

import { Message, SenderStatus } from '../../types';
import MessageSubTypes from '../../models/enums/MessageSubTypes';

import MessageAttachment from './MessageAttachment';
import MessageContent from './MessageContent';
import MessageDetails from './MessageDetails';
import MessageHeader from './MessageHeader';

import styles from './MessageBubble.module.css';

export function MessageBubble({
  message,
  noReply,
  timelineRef,
  messageIdSnapshot,
}: {
  message: Message;
  noReply: boolean;
  timelineRef: React.RefObject<HTMLElement>;
  messageIdSnapshot: string[];
}) {
  if (!message) return null;
  const { body, isOutgoing, sender, senderStatus, subType } = message;
  const isAwayMessage = subType === MessageSubTypes.USER_AWAY_NOTIFICATION;
  const isSmsAutomatedOptMessage =
    subType === MessageSubTypes.SMS_OPT_OUT_NOTIFICATION ||
    subType === MessageSubTypes.SMS_OPT_IN_NOTIFICATION;
  const isVwrBotMessage = subType === MessageSubTypes.CHATBOT_MESSAGE;
  const shouldShowDisplayName =
    !isOutgoing && !isVwrBotMessage && !isSmsAutomatedOptMessage && sender;

  const isFailedOrRetrying =
    senderStatus === SenderStatus.Failed || senderStatus === SenderStatus.Retrying;
  let messageStyleType;
  if (isOutgoing) {
    if (isFailedOrRetrying) {
      messageStyleType = 'failedOutgoingMessage';
    } else {
      messageStyleType = 'outgoingMessage';
    }
  } else {
    messageStyleType = 'incomingMessage';
  }

  return (
    <div
      className={classNames(styles.messageBubble, styles[messageStyleType], {
        [styles.priorityMessage]: isSmsAutomatedOptMessage,
        [styles.vwrBotMessage]: isVwrBotMessage,
      })}
      aria-label="message-bubble"
    >
      <MessageHeader message={message} />
      <div className={styles.messageContentContainer}>
        {shouldShowDisplayName && (
          <div className={styles.senderName} aria-label="sender-name">
            {sender.displayName}
          </div>
        )}
        <MessageContent content={body} isAwayMessage={isAwayMessage} />
        {noReply && <div className={styles.noReplyMessage}>This is a no-reply message</div>}
        {message?.attachments?.length > 0 && (
          <MessageAttachment
            attachment={message.attachments[0]}
            messageId={message.serverId || message.id}
            timelineRef={timelineRef}
            messageIdSnapshot={messageIdSnapshot}
          />
        )}
        <MessageDetails message={message} />
      </div>
    </div>
  );
}

export default MessageBubble;
